import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * DeferUntilVisible — mounts children only once they approach the viewport.
 *
 * Unlike MobileDefer (mobile-only, already-rendered content), this never
 * renders the subtree during SSR/hydration, so below-the-fold widgets that do
 * their own data fetching (related articles, cross-link grids) cost nothing on
 * first paint. A reserved min-height + `content-visibility: auto` keeps layout
 * stable (no CLS) and lets the browser skip layout/paint until needed.
 */
export function DeferUntilVisible({
  children,
  minHeight = 320,
  rootMargin = "600px 0px",
}: {
  children: ReactNode;
  minHeight?: number;
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          setVisible(true);
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible, rootMargin]);

  return (
    <div
      ref={ref}
      style={
        visible
          ? undefined
          : {
              minHeight: `${minHeight}px`,
              contentVisibility: "auto",
              containIntrinsicSize: `${minHeight}px`,
            }
      }
    >
      {visible ? children : null}
    </div>
  );
}

export default DeferUntilVisible;
